// src/utils/printReport.ts
/**
 * Shared print utility for all reports and data tables.
 *
 * Any page that wants to print:
 *   1. Wrap the printable content in a `<div className="print-area">...</div>`
 *   2. Call `printReport()` from the Print icon click handler
 *
 * The @media print rules in src/styles/print.css hide everything outside
 * .print-area (sidebar, nav, toolbars, page chrome), so the native browser
 * print dialog shows only the document itself.
 *
 * Works for both printing and "Save as PDF" (built-in to the native dialog).
 */

export interface PrintOptions {
  /** Optional title to set on the document before printing (shows in PDF filename). */
  documentTitle?: string;
}

export function printReport(options: PrintOptions = {}): void {
  const originalTitle = document.title;

  if (options.documentTitle) {
    document.title = options.documentTitle;
  }

  // Trigger the browser's native print dialog
  window.print();

  // Restore the original document title after the dialog closes
  // Use a small timeout so the title is still set when the dialog captures it
  if (options.documentTitle) {
    setTimeout(function () {
      document.title = originalTitle;
    }, 500);
  }
}

/**
 * Build a tidy filename for a printed/exported report.
 * Example: buildReportFileName('Account List') => 'Account_List_2026-10-25'
 */
export function buildReportFileName(reportName: string, date: Date = new Date()): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const slug = reportName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
  return slug + '_' + yyyy + '-' + mm + '-' + dd;
}